// El calendario de las noches de hotel.
//
// Sólo aparece en una rama concreta, y por eso es fácil no verlo nunca: "Tren + Hotel / Ocio"
// + viaje de SÓLO IDA + decir que sí al hotel. Como no hay vuelta del tren, no hay forma de
// saber cuándo se va del hotel, así que se pregunta.
//
// Arranca en ida+1 / ida+2 y no deja elegir antes de la ida — de ahí sale el `dateTo=13-09`
// del escenario C de F0 con una ida el 11.
//
// El del coche de alquiler del tren + coche es el mismo calendario con otro título: en sólo
// ida tampoco hay vuelta del tren que diga hasta cuándo se quiere el coche.
import { modalConPlantilla } from './modales.js'
import { pintarCalendario, leerClic } from './calendario-render.js'
import { formatear, sumarDias, antesDe } from './fechas.js'

/**
 * @param {import('./buscador.js').Buscador} buscador
 * @param {object} estado                   el del bloque, para leer la fecha de ida
 * @param {(salida: Date) => void} alAceptar
 */
export function crearCalendarioHotel (buscador, estado, alAceptar) {
  return crearCalendarioRango(buscador, {
    plantilla: 'calendarioSalidaHotel',
    minimo: estado.fechaIda,
    entrada: sumarDias(estado.fechaIda, 1),
    salida: sumarDias(estado.fechaIda, 2),
  }, (entrada, salida) => alAceptar(salida))
}

/**
 * Recogida y devolución del coche del tren + coche en sólo ida. Arranca en ida / ida+1, que es
 * lo que se proponía antes sin preguntar, y no deja recogerlo antes de que llegue el tren.
 *
 * @param {import('./buscador.js').Buscador} buscador
 * @param {object} estado                   el del bloque, para leer la fecha de ida
 * @param {(recogida: Date, devolucion: Date) => void} alAceptar
 */
export function crearCalendarioCoche (buscador, estado, alAceptar) {
  return crearCalendarioRango(buscador, {
    plantilla: 'calendarioCoche',
    minimo: estado.fechaIda,
    entrada: estado.fechaIda,
    salida: sumarDias(estado.fechaIda, 1),
  }, alAceptar)
}

/**
 * @param {import('./buscador.js').Buscador} buscador
 * @param {{plantilla: string, minimo: Date, entrada: Date, salida: Date}} opciones
 * @param {(entrada: Date, salida: Date) => void} alAceptar
 */
function crearCalendarioRango (buscador, opciones, alAceptar) {
  // Las dos plantillas comparten la clase: el coche se ve igual que el hotel.
  const modal = modalConPlantilla(buscador, opciones.plantilla, 'rvb-swal-fechas-hotel')
  if (!modal) {
    // Sin plantilla no se puede preguntar: se sigue con las fechas de partida.
    alAceptar(opciones.entrada, opciones.salida)
    return null
  }

  const minimo = opciones.minimo
  let entrada = opciones.entrada
  let salida = opciones.salida
  let eligiendo = 'entrada'
  let mesVisible = new Date(entrada.getFullYear(), entrada.getMonth(), 1)

  const contenedor = modal.contenido.querySelector('.rvb-datepicker-ida-vuelta')
  // El calendario nace oculto (el CSS sólo lo enseña bajo .rvb-calendarActive): en el modal
  // tiene que verse desde el principio, que es para lo que se abre.
  if (contenedor && contenedor.parentElement && contenedor.parentElement.parentElement) {
    contenedor.parentElement.parentElement.classList.add('rvb-calendarActive')
  }
  const campoEntrada = modal.contenido.querySelector('.rvb-input-daterange .rvb-dateUserFrom')
  const campoSalida = modal.contenido.querySelector('.rvb-input-daterange .rvb-dateUserTo')

  function pintar () {
    if (contenedor) {
      contenedor.innerHTML = pintarCalendario(mesVisible, { ida: entrada, vuelta: salida, minimo })
    }
    if (campoEntrada) campoEntrada.value = formatear(entrada)
    if (campoSalida) campoSalida.value = formatear(salida)
  }

  if (contenedor) {
    contenedor.addEventListener('click', (e) => {
      const accion = leerClic(e)
      if (!accion) return
      if (accion.mes) {
        mesVisible = new Date(mesVisible.getFullYear(), mesVisible.getMonth() + accion.mes, 1)
        pintar()
        return
      }
      if (eligiendo === 'entrada') {
        entrada = accion.dia
        if (!antesDe(entrada, salida)) salida = sumarDias(entrada, 1)
        eligiendo = 'salida'
      } else if (antesDe(accion.dia, entrada)) {
        entrada = accion.dia
        salida = sumarDias(accion.dia, 1)
        eligiendo = 'salida'
      } else {
        salida = accion.dia
        eligiendo = 'entrada'
      }
      pintar()
    })
  }

  // El botón de aceptar de esta plantilla es el "mobile", que es el que la home usa como
  // texto del botón de confirmar del modal.
  modal.contenido.querySelectorAll('.rvb-btn-aceptar-fechas').forEach((b) => {
    b.addEventListener('click', (e) => {
      e.preventDefault()
      modal.cerrar()
      alAceptar(entrada, salida)
    })
  })

  pintar()
  return modal
}
